// Paddle owns card entry and payment state. This module loads its official SDK;
// the server-created transaction carries the tenant/vertical attribution.
export interface PaddleEvent { name?: string }
interface PaddleClient {
  Environment: { set(environment: 'sandbox' | 'production'): void };
  Initialize(options: { token: string; checkout: { settings: { displayMode: 'overlay'; variant: 'one-page' } }; eventCallback: (event: PaddleEvent) => void }): void;
  Checkout: { open(options: { transactionId: string }): void };
}
declare global { interface Window { Paddle?: PaddleClient } }

let ready: Promise<PaddleClient> | undefined;
let currentTransaction: string | undefined;
let lastEvent: PaddleEvent | undefined;
const listeners = new Set<(event: PaddleEvent) => void>();

export function validPaddleTransaction(value: string | null): value is string {
  return value !== null && /^txn_[a-z0-9]{26}$/.test(value);
}

export function connectPaddleCheckout(transactionId: string, listener: (event: PaddleEvent) => void) {
  listeners.add(listener);
  const environment: unknown = import.meta.env.VITE_PADDLE_ENVIRONMENT;
  const token: unknown = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
  if ((environment !== 'sandbox' && environment !== 'production') || typeof token !== 'string'
    || !token.startsWith(environment === 'sandbox' ? 'test_' : 'live_')) {
    return { ready: Promise.reject(new Error('Checkout is not configured. Please contact support.')), disconnect: () => { listeners.delete(listener); } };
  }
  if (!ready) {
    ready = new Promise<PaddleClient>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;
      let settled = false;
      const timeout = window.setTimeout(failed, 15000);
      function failed() {
        if (settled) return;
        settled = true;
        script.onload = null;
        script.onerror = null;
        window.clearTimeout(timeout);
        script.remove();
        reject(new Error('Checkout could not load. Please try again.'));
      }
      const loaded = () => {
        if (settled) return;
        window.clearTimeout(timeout);
        try {
          const paddle = window.Paddle;
          if (!paddle) { failed(); return; }
          paddle.Environment.set(environment);
          // _ptxn is read by Paddle.js itself and opens exactly one checkout.
          currentTransaction = transactionId;
          paddle.Initialize({ token, checkout: { settings: { displayMode: 'overlay', variant: 'one-page' } },
            eventCallback: (event) => { lastEvent = event; for (const callback of listeners) callback(event); } });
          settled = true;
          resolve(paddle);
        } catch { failed(); }
      };
      if (window.Paddle) loaded();
      else {
        script.onload = loaded;
        script.onerror = failed;
        document.head.append(script);
      }
    }).catch((error: unknown) => { ready = undefined; throw error; });
  }
  const connected = ready.then((paddle) => {
    if (!listeners.has(listener)) return paddle;
    // SPA remounts reuse initialization. A different transaction opens explicitly;
    // StrictMode's duplicate effect sees the same ID and cannot double-open it.
    if (currentTransaction !== transactionId) {
      currentTransaction = transactionId;
      lastEvent = undefined;
      paddle.Checkout.open({ transactionId });
    } else if (lastEvent) listener(lastEvent);
    return paddle;
  });
  return { ready: connected, disconnect: () => { listeners.delete(listener); } };
}

/** Explicit retries use the same transaction/event bookkeeping as SPA changes. */
export async function reopenPaddleCheckout(transactionId: string): Promise<void> {
  if (!ready || !validPaddleTransaction(transactionId)) throw new Error('Checkout is unavailable.');
  const paddle = await ready;
  currentTransaction = transactionId;
  lastEvent = undefined;
  paddle.Checkout.open({ transactionId });
}
